import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Workflow, Clock, Loader2, Trash2 } from 'lucide-react';
import api from '../utils/api';

const GRADIENTS = [
  'from-[#8c7db0] via-[#7fafa8] to-[#687f94]',
  'from-[#c2a079] via-[#8c7db0] to-[#7fafa8]',
  'from-[#7fafa8] via-[#8aa0b5] to-[#6f6196]',
  'from-[#b98f98] via-[#c2a079] to-[#8c7db0]',
  'from-[#c2a079] via-[#a89272] to-[#7fafa8]',
  'from-[#6ba088] via-[#7fafa8] to-[#8aa0b5]',
];

function timeAgo(dateStr) {
  const s = (Date.now() - new Date(dateStr)) / 1000;
  if (s < 60)    return '刚刚';
  if (s < 3600)  return `${Math.floor(s / 60)} 分钟前`;
  if (s < 86400) return `${Math.floor(s / 3600)} 小时前`;
  return `${Math.floor(s / 86400)} 天前`;
}

export default function CanvasListPage() {
  const [canvases, setCanvases] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/canvas').then(r => setCanvases(r.data)).finally(() => setLoading(false));
  }, []);

  const createCanvas = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post('/canvas', { name: '未命名画布' });
      navigate(`/canvas/${r.data.id}`);
    } finally {
      setCreating(false);
    }
  };

  const [deletingId, setDeletingId] = useState(null);
  const deleteCanvas = async (e, c) => {
    e.stopPropagation(); // 不要触发卡片的"进入画布"
    if (deletingId) return;
    if (!window.confirm(`确定删除画布「${c.name || '未命名画布'}」吗？\n画布里的所有节点、图片、视频都会一并删除，且无法恢复。`)) return;
    setDeletingId(c.id);
    try {
      await api.delete(`/canvas/${c.id}`);
      setCanvases(prev => prev.filter(x => x.id !== c.id));
    } catch (err) {
      alert(err.response?.data?.error || '删除失败，请重试');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen p-8 bg-[#f4f1e9] dark:bg-[#0e0e10]">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#eee9de] dark:bg-white/[0.06] flex items-center justify-center">
              <Workflow size={15} className="text-[#6f6196] dark:text-[#b9afe7]" />
            </div>
            <h1 className="text-[18px] font-semibold text-gray-900 dark:text-white">无限画布</h1>
          </div>
          <button
            onClick={createCanvas}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-50 text-white text-[13px] font-medium transition-colors"
          >
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            新建画布
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
            <Loader2 size={16} className="animate-spin" /> 加载中…
          </div>
        )}

        {/* Empty state */}
        {!loading && canvases.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Workflow size={36} className="mb-4 opacity-25" />
            <p className="text-[14px] mb-6">还没有画布</p>
            <button
              onClick={createCanvas}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] text-white text-[13px] font-medium transition-colors"
            >
              <Plus size={13} /> 新建第一个画布
            </button>
          </div>
        )}

        {/* Grid */}
        {!loading && canvases.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* New canvas card */}
            <button
              onClick={createCanvas}
              className="rounded-2xl border-2 border-dashed border-[#ded6c8] dark:border-white/[0.08] hover:border-[#c9bedc] dark:hover:border-white/[0.16] flex flex-col items-center justify-center gap-3 text-stone-400 hover:text-[#6f6196] dark:hover:text-[#b9afe7] transition-all group"
              style={{ aspectRatio: '4/3' }}
            >
              <div className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center group-hover:scale-110 transition-transform">
                <Plus size={18} />
              </div>
              <span className="text-[12px] font-medium">新建画布</span>
            </button>

            {/* Canvas cards */}
            {canvases.map((c, i) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/canvas/${c.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/canvas/${c.id}`); }}
                className="rounded-2xl overflow-hidden relative group shadow-sm hover:shadow-xl transition-all duration-200 cursor-pointer"
                style={{ aspectRatio: '4/3' }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${GRADIENTS[i % GRADIENTS.length]}`} />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                {/* 删除按钮：悬停时显示，右上角 */}
                <button
                  onClick={(e) => deleteCanvas(e, c)}
                  disabled={deletingId === c.id}
                  title="删除画布"
                  className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/35 hover:bg-red-500 text-white/90 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all disabled:opacity-100"
                >
                  {deletingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </button>
                {/* Canvas name overlay */}
                <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 bg-gradient-to-t from-black/70 to-transparent text-left">
                  <p className="text-white text-[13px] font-medium truncate leading-tight">{c.name}</p>
                  <div className="flex items-center gap-1 mt-0.5 text-white/50 text-[10px]">
                    <Clock size={9} />
                    <span>{timeAgo(c.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
